import { Node, mergeAttributes } from '@tiptap/core';
import { ReactRenderer } from '@tiptap/react';
import React from 'react';
import { FileText, BookOpen } from 'lucide-react';

export interface ReferenceBlockAttributes {
  id: string;
  type: 'guide' | 'article';
  title: string;
  subtitle?: string;
  url?: string;
  helpCenterSlug?: string;
}

export const ReferenceBlock = Node.create({
  name: 'referenceBlock',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  group: 'block',

  content: '',

  atom: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: element => element.getAttribute('data-id'),
        renderHTML: attributes => {
          if (!attributes.id) {
            return {};
          }
          return {
            'data-id': attributes.id,
          };
        },
      },
      type: {
        default: 'guide',
        parseHTML: element => element.getAttribute('data-type') || 'guide',
        renderHTML: attributes => {
          return {
            'data-type': attributes.type,
          };
        },
      },
      title: {
        default: '',
        parseHTML: element => element.getAttribute('data-title') || '',
        renderHTML: attributes => {
          if (!attributes.title) {
            return {};
          }
          return {
            'data-title': attributes.title,
          };
        },
      },
      subtitle: {
        default: null,
        parseHTML: element => element.getAttribute('data-subtitle'),
        renderHTML: attributes => {
          if (!attributes.subtitle) {
            return {};
          }
          return {
            'data-subtitle': attributes.subtitle,
          };
        },
      },
      url: {
        default: null,
        parseHTML: element => element.getAttribute('data-url'),
        renderHTML: attributes => {
          if (!attributes.url) {
            return {};
          }
          return {
            'data-url': attributes.url,
          };
        },
      },
      helpCenterSlug: {
        default: null,
        parseHTML: element => element.getAttribute('data-help-center-slug'),
        renderHTML: attributes => {
          if (!attributes.helpCenterSlug) {
            return {};
          }
          return {
            'data-help-center-slug': attributes.helpCenterSlug,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="reference-block"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes, node }) {
    const attrs = node.attrs as ReferenceBlockAttributes;
    
    const containerContent: any[] = [
      [
        'div',
        { class: 'reference-block-icon' },
        attrs.type === 'guide' ? '📚' : '📄',
      ],
      [
        'div',
        { class: 'reference-block-content' },
        [
          'div',
          { class: 'reference-block-title' },
          attrs.title || 'Untitled',
        ],
      ],
    ];

    // Add subtitle if present
    if (attrs.subtitle) {
      containerContent[1][2].push([
        'div',
        { class: 'reference-block-subtitle' },
        attrs.subtitle,
      ]);
    }

    const container = [
      'div',
      { class: 'reference-block-container' },
      ...containerContent,
    ];
    
    // If URL exists, wrap in anchor tag for folge-cloud
    if (attrs.url) {
      return [
        'a',
        {
          href: attrs.url,
          target: '_blank',
          class: 'reference-block-link',
        },
        [
          'div',
          mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
            'data-type': 'reference-block',
            'data-id': attrs.id || '',
            'data-reference-type': attrs.type || 'guide',
            'data-url': attrs.url || '',
            'data-help-center-slug': attrs.helpCenterSlug || '',
            class: 'reference-block',
          }),
          container,
        ],
      ];
    }
    
    return [
      'div',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-type': 'reference-block',
        'data-id': attrs.id || '',
        'data-reference-type': attrs.type || 'guide',
        'data-url': attrs.url || '',
        'data-help-center-slug': attrs.helpCenterSlug || '',
        class: 'reference-block',
      }),
      container,
    ];
  },

  addNodeView() {
    return ({ node, HTMLAttributes }) => {
      const dom = document.createElement('div');
      
      dom.setAttribute('data-type', 'reference-block');
      dom.className = 'reference-block';
      Object.keys(HTMLAttributes).forEach(key => {
        dom.setAttribute(key, HTMLAttributes[key]);
      });

      const attrs = node.attrs as ReferenceBlockAttributes;

      const component = new ReactRenderer(ReferenceBlockComponent, {
        props: {
          id: attrs.id,
          type: attrs.type,
          title: attrs.title,
          subtitle: attrs.subtitle,
          url: attrs.url,
          helpCenterSlug: attrs.helpCenterSlug,
        },
        editor: this.editor,
      });

      dom.appendChild(component.element);

      return {
        dom,
        contentDOM: null,
        destroy: () => {
          component.destroy();
        },
      };
    };
  },
});

const ReferenceBlockComponent: React.FC<ReferenceBlockAttributes> = ({
  type,
  title,
}) => {
  const Icon = type === 'guide' ? BookOpen : FileText;

  return (
    <div className="reference-block-container my-4 flex items-center px-3 py-3 bg-white border border-gray-200 rounded-lg shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
      {/* Icon Circle */}
      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mr-4 flex-shrink-0">
        <Icon size={24} className="text-gray-500" />
      </div>

      {/* Text Content */}
      <div className="flex-1 min-w-0">
        <div className="text-base font-semibold text-gray-900 mb-1 leading-normal">
          {title || 'Untitled'}
        </div>
      </div>
    </div>
  );
};
